import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Lock, Check, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { PLAN_LABELS, PLAN_PRICES, type PlanType } from "@/lib/subscriptionContext";
import { useAuth } from "@/lib/authContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Oplif brand green
const BRAND_GREEN = "#2d8653";

interface FeatureUpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  featureName: string;
  requiredPlan: Extract<PlanType, "pro" | "business">;
  perks?: string[];
}

const DEFAULT_PERKS: Record<string, string[]> = {
  pro: [
    "Full opening timeline, permits & budget",
    "Location & neighborhood recommendations",
    "Equipment planner & vendor links",
    "Staff rota & scheduling",
    "Operations suite & survival score",
  ],
  business: [
    "Everything in Pro",
    "Multi-location portfolio",
    "White-label exports",
    "Up to 5 team seats",
  ],
};

export default function FeatureUpgradeModal({
  open,
  onOpenChange,
  featureName,
  requiredPlan,
  perks,
}: FeatureUpgradeModalProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [annual, setAnnual] = useState(false);
  const [loading, setLoading] = useState(false);
  const planLabel = PLAN_LABELS[requiredPlan];
  const monthly = PLAN_PRICES[requiredPlan]?.monthly ?? 0;
  const yearlyTotal = PLAN_PRICES[requiredPlan]?.yearly ?? 0;
  const yearlyPerMonth = yearlyTotal > 0 ? Math.round((yearlyTotal / 12) * 10) / 10 : 0;
  const displayPrice = annual ? Math.floor(yearlyPerMonth) : monthly;
  const list = perks ?? DEFAULT_PERKS[requiredPlan] ?? [];

  const handleUpgrade = async () => {
    if (!user) {
      onOpenChange(false);
      navigate("/auth");
      return;
    }
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { plan: requiredPlan, billing: annual ? "yearly" : "monthly", origin: window.location.origin },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url as string;
        return;
      }
      throw new Error("No checkout URL");
    } catch (err) {
      console.error(err);
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        {/* Brand green header */}
        <div
          className="px-6 pt-6 pb-5 text-white"
          style={{ background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #246d43 100%)` }}
        >
          <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center mb-3 backdrop-blur-sm">
            <Lock className="w-5 h-5" />
          </div>
          <DialogHeader className="space-y-1.5 text-left">
            <DialogTitle className="font-heading text-xl text-white">
              Unlock this feature
            </DialogTitle>
            <DialogDescription className="text-white/85 text-sm">
              Upgrade your plan to access{" "}
              <span className="font-medium text-white">{featureName}</span>.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Required plan
              </div>
              <div className="font-heading text-lg font-medium text-foreground flex items-center gap-2">
                {planLabel}
                <Sparkles className="w-4 h-4" style={{ color: BRAND_GREEN }} />
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading text-2xl font-medium text-foreground">
                ${displayPrice}
                <span className="text-sm font-normal text-muted-foreground">/mo</span>
              </div>
              <div className="text-[11px] text-muted-foreground">
                {annual ? `$${yearlyTotal} billed yearly` : "Cancel anytime"}
              </div>
            </div>
          </div>

          {/* Monthly / Yearly toggle */}
          <div className="flex items-center justify-center gap-2 mb-4 py-2 px-3 rounded-lg bg-muted/40">
            <span className={`text-xs font-medium ${!annual ? "text-foreground" : "text-muted-foreground"}`}>Monthly</span>
            <Switch checked={annual} onCheckedChange={setAnnual} />
            <span className={`text-xs font-medium ${annual ? "text-foreground" : "text-muted-foreground"}`}>Yearly</span>
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full text-white ml-1"
              style={{ backgroundColor: BRAND_GREEN }}
            >
              Save 2 months
            </span>
          </div>

          {list.length > 0 && (
            <ul className="space-y-2 mb-5">
              {list.map((perk) => (
                <li key={perk} className="flex items-start gap-2 text-sm text-foreground">
                  <Check
                    className="w-4 h-4 mt-0.5 shrink-0"
                    style={{ color: BRAND_GREEN }}
                  />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
          )}

          <Button
            className="w-full gap-2 text-white hover:brightness-110 border-0"
            style={{ backgroundColor: BRAND_GREEN }}
            onClick={handleUpgrade}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Redirecting…
              </>
            ) : (
              <>
                Upgrade now <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
          <button
            onClick={() => onOpenChange(false)}
            className="w-full mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors py-1.5"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
